/**
 * Pure, read-only derivations over GameState. UI and AI read the game through
 * these instead of poking at the board directly, so display logic stays out of
 * components.
 */
import { pieceDef } from "./pieces";
import { isInCheck, isPrinceAlive, isPrinceThreatened } from "./rules";
import type { GameState, PieceTypeId, PlayerId, Piece } from "./types";

/** Total material value of a player's pieces on the board. */
export function materialValue(state: GameState, owner: PlayerId): number {
  let total = 0;
  for (const p of state.board) {
    if (p && p.owner === owner) total += pieceDef(p.type).value;
  }
  return total;
}

/** Count of each piece type a player has on the board. */
export function pieceCounts(state: GameState, owner: PlayerId): Map<PieceTypeId, number> {
  const counts = new Map<PieceTypeId, number>();
  for (const p of state.board) {
    if (p && p.owner === owner) counts.set(p.type, (counts.get(p.type) ?? 0) + 1);
  }
  return counts;
}

export interface RoyaltyStatus {
  readonly princeAlive: boolean;
  /** True when this player's King is currently in check. */
  readonly inCheck: boolean;
  /** True when this player's (living) Prince is under attack — guard it. */
  readonly princeThreatened: boolean;
}

export function royaltyStatus(state: GameState, owner: PlayerId): RoyaltyStatus {
  return {
    princeAlive: isPrinceAlive(state, owner),
    inCheck: isInCheck(state, owner),
    princeThreatened: isPrinceThreatened(state, owner),
  };
}

export function allPieces(state: GameState): { square: number; piece: Piece }[] {
  const out: { square: number; piece: Piece }[] = [];
  state.board.forEach((piece, square) => {
    if (piece) out.push({ square, piece });
  });
  return out;
}
